/**
 * PostalcodeWidget component.
 *
 * A plain text widget (same markup / styling / clear button as the customised
 * TextWidget) that opens the numeric keyboard on iOS / Android by setting
 * `inputmode="numeric"` and `pattern="[0-9]*"`.
 *
 * @module components/Widgets/PostalcodeWidget/PostalcodeWidget
 */

import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Input } from 'semantic-ui-react';

import Icon from '@plone/volto/components/theme/Icon/Icon';
import FormFieldWrapper from '@plone/volto/components/manage/Widgets/FormFieldWrapper';

import clearSVG from '@plone/volto/icons/clear.svg';

const PostalcodeWidget = (props) => {
  const {
    id,
    value,
    onChange,
    onBlur,
    onClick,
    minLength,
    maxLength,
    placeholder,
    isDisabled,
    focus,
    required,
    error,
    resettable,
  } = props;

  const ref = useRef();

  useEffect(() => {
    if (focus) {
      ref.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onReset = () => onChange(id, null);

  return (
    <FormFieldWrapper {...props} className="text">
      <div className="date-time-widget-wrapper text-widget-wrapper">
        <Input
          id={`field-${id}`}
          name={id}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={value || ''}
          disabled={isDisabled}
          placeholder={placeholder}
          onChange={({ target }) =>
            onChange(id, target.value === '' ? undefined : target.value)
          }
          ref={ref}
          onBlur={({ target }) =>
            onBlur(id, target.value === '' ? undefined : target.value)
          }
          onClick={() => onClick()}
          minLength={minLength || null}
          maxLength={maxLength || null}
          aria-required={required ? 'true' : undefined}
          aria-invalid={error?.length > 0 ? 'true' : undefined}
        />
        {resettable && value && (
          <button
            type="button"
            disabled={isDisabled}
            onClick={onReset}
            className="item ui noborder button"
            aria-label="Clear"
          >
            <Icon name={clearSVG} size="24px" className="close" />
          </button>
        )}
      </div>
    </FormFieldWrapper>
  );
};

PostalcodeWidget.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.arrayOf(PropTypes.string),
  value: PropTypes.string,
  focus: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onClick: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  minLength: PropTypes.number,
  maxLength: PropTypes.number,
  wrapped: PropTypes.bool,
  placeholder: PropTypes.string,
  resettable: PropTypes.bool,
};

PostalcodeWidget.defaultProps = {
  description: null,
  required: false,
  error: [],
  value: null,
  onChange: () => {},
  onBlur: () => {},
  onClick: () => {},
  onEdit: null,
  onDelete: null,
  focus: false,
  minLength: null,
  maxLength: null,
  resettable: true,
};

export default PostalcodeWidget;
